import { Link } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';

export default function Cart() {
  const { cart, removeItem, updateQuantity, clearCart, cartTotal } = useCart();

  if (cart.length === 0) {
    return (
      <main className="min-h-screen bg-[#f0f4f7]">
        <section className="bg-[#2c2a4a] py-14 md:py-20">
          <div className="container-main text-center">
            <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3">Shopping Cart</h1>
            <p className="text-white/70 text-base md:text-lg">Your garden art collection awaits.</p>
          </div>
        </section>

        <section className="py-20 md:py-28 text-center">
          <div className="container-main">
            <div className="w-20 h-20 bg-[#d1bedd] rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-shopping-basket-line text-3xl text-[#88d6f8]" />
            </div>
            <h2 className="font-serif text-2xl font-bold text-[#1A1A1A] mb-2">Your cart is empty</h2>
            <p className="text-[#8B7355] mb-8 max-w-md mx-auto">
              Looks like you have not added any wind spinners or garden art yet. Explore our collections and find the perfect piece for your space.
            </p>
            <Link
              to="/shop"
              className="inline-block py-3.5 px-10 rounded-md bg-[#4f528d] text-white font-sans font-semibold text-sm uppercase tracking-wider hover:bg-[#3d4070] transition-colors whitespace-nowrap"
            >
              Continue Shopping
            </Link>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#f0f4f7]">
      {/* Header */}
      <section className="bg-[#2c2a4a] py-14 md:py-20">
        <div className="container-main text-center">
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3">Shopping Cart</h1>
          <p className="text-white/70 text-base md:text-lg">
            {cart.reduce((s, i) => s + i.quantity, 0)} item{cart.reduce((s, i) => s + i.quantity, 0) !== 1 ? 's' : ''}
          </p>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-xl font-bold text-[#1A1A1A]">Items</h2>
                <button
                  onClick={clearCart}
                  className="text-sm text-[#8B7355] hover:text-red-600 transition-colors flex items-center gap-1"
                >
                  <i className="ri-delete-bin-line" /> Clear cart
                </button>
              </div>

              {cart.map((item) => (
                <div
                  key={`${item.productId}-${item.skuId ?? 'base'}`}
                  className="flex flex-col sm:flex-row gap-4 p-4 md:p-5 bg-[#efe4f7] rounded-lg border border-[#E8E0D4]"
                >
                  <Link to={`/product/${item.productId}`} className="shrink-0">
                    <div className="w-full sm:w-24 h-24 rounded-md overflow-hidden bg-white border border-[#E8E0D4]">
                      {item.image && (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  </Link>

                  <div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                    <div className="flex-1">
                      <Link
                        to={`/product/${item.productId}`}
                        className="font-serif text-base md:text-lg font-semibold text-[#1A1A1A] hover:text-[#4f528d] transition-colors leading-snug"
                      >
                        {item.name}
                      </Link>
                      {item.skuLabel && (
                        <p className="text-xs text-[#4f528d] font-medium mt-0.5">{item.skuLabel}</p>
                      )}
                      <p className="text-[#4f528d] font-semibold mt-1">${item.price.toFixed(2)} each</p>
                    </div>

                    <div className="flex items-center gap-4">
                      {/* Quantity */}
                      <div className="flex items-center border border-[#D4C9B8] rounded-md overflow-hidden bg-white">
                        <button
                          onClick={() => updateQuantity(item.productId, item.skuId, item.quantity - 1)}
                          className="px-3 py-2 hover:bg-[#d1bedd] transition-colors text-[#1A1A1A]"
                        >
                          <i className="ri-subtract-line" />
                        </button>
                        <span className="px-4 py-2 text-sm font-semibold text-[#1A1A1A] min-w-[3rem] text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.productId, item.skuId, item.quantity + 1)}
                          className="px-3 py-2 hover:bg-[#d1bedd] transition-colors text-[#1A1A1A]"
                        >
                          <i className="ri-add-line" />
                        </button>
                      </div>

                      {/* Line total */}
                      <p className="text-base md:text-lg font-bold text-[#2c2a4a] min-w-[5rem] text-right">
                        ${(item.price * item.quantity).toFixed(2)}
                      </p>

                      {/* Remove */}
                      <button
                        onClick={() => removeItem(item.productId, item.skuId)}
                        className="p-2 text-[#8B7355] hover:text-red-600 transition-colors"
                        title="Remove item"
                      >
                        <i className="ri-delete-bin-line text-lg" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <div className="pt-4">
                <Link
                  to="/shop"
                  className="text-sm text-[#4f528d] hover:underline flex items-center gap-1"
                >
                  <i className="ri-arrow-left-line" /> Continue Shopping
                </Link>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-[#efe4f7] rounded-lg border border-[#E8E0D4] p-6 sticky top-24">
                <h2 className="font-serif text-xl font-bold text-[#1A1A1A] mb-5">Order Summary</h2>

                <div className="space-y-3 text-sm">
                  <div className="flex justify-between text-[#4A4A4A]">
                    <span>Subtotal</span>
                    <span>${cartTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[#4A4A4A]">
                    <span>Shipping</span>
                    <span>{cartTotal >= 50 ? 'Free' : 'Calculated at checkout'}</span>
                  </div>
                </div>

                <div className="border-t border-[#E8E0D4] pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-serif text-lg font-bold text-[#1A1A1A]">Total</span>
                    <span className="font-serif text-xl font-bold text-[#2c2a4a]">${cartTotal.toFixed(2)}</span>
                  </div>
                </div>

                {cartTotal < 50 && (
                  <p className="text-xs text-[#8B7355] mt-3">
                    Add ${(50 - cartTotal).toFixed(2)} more for free shipping!
                  </p>
                )}

                <Link
                  to="/checkout"
                  className="block w-full mt-6 py-3.5 px-8 rounded-md bg-[#4f528d] text-white font-sans font-semibold text-sm uppercase tracking-wider hover:bg-[#3d4070] transition-colors text-center whitespace-nowrap"
                >
                  Proceed to Checkout
                </Link>

                <div className="mt-5 flex items-center justify-center gap-2 text-xs text-[#8B7355]">
                  <i className="ri-truck-line" />
                  <span>Ships from Madison, WI</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}